/**
 * The individual flights a record makes during a swap: off the spindle, in
 * from the crate, and the damped wobble as it beds down.
 *
 * Each stage is an awaited tween tracked by a cancellation token, so a swap
 * superseded mid-flight abandons cleanly rather than leaving a half-animated
 * disc in the scene.
 */

import { DIM } from '../geometry/dims.ts';
import { ease } from '../lib/ease.ts';
import { lerp } from '../lib/math.ts';
import type { Palette } from '../lib/palette.ts';
import { CancelledError, type Token, tween } from '../lib/tween.ts';
import type { RecordMesh } from './record.ts';

export interface SwapArt {
  image: CanvasImageSource | null;
  palette: Palette | null;
  pictureDisc: boolean;
}

/** Drops a record straight onto the spindle with no animation. */
export function seatRecord(disc: RecordMesh, art: SwapArt): void {
  disc.setArtwork(art.image, art.palette, art.pictureDisc);
  disc.carrier.position.set(DIM.platter.x, DIM.recordY, DIM.platter.z);
  disc.carrier.rotation.set(0, 0, 0);
  disc.carrier.scale.setScalar(1);
  disc.setOpacity(1);
}

/** The outgoing record lifts, tips, and slides away toward the crate. */
export async function flyOut(disc: RecordMesh, token: Token): Promise<void> {
  const from = disc.carrier.position.clone();
  await token.track(
    tween({
      duration: 620,
      ease: ease.inOutCubic,
      onUpdate: (_v, t) => {
        disc.carrier.position.set(
          from.x - 3 * t,
          from.y + 9 * t + Math.sin(t * Math.PI) * 2.5,
          from.z - 26 * t,
        );
        disc.carrier.rotation.x = -0.3 * t;
        disc.carrier.rotation.z = 0.12 * t;
        disc.setOpacity(1 - Math.max(0, (t - 0.45) / 0.55));
        disc.carrier.scale.setScalar(1 - 0.06 * t);
      },
    }),
  );
  token.check();
}

/** The incoming record comes in from the upper right and lands on the spindle. */
export async function flyIn(disc: RecordMesh, token: Token): Promise<void> {
  disc.setOpacity(0);
  disc.carrier.position.set(DIM.platter.x + 4, DIM.recordY + 16, DIM.platter.z + 22);
  disc.carrier.rotation.set(0.34, 0, -0.14);

  await token.track(
    tween({
      duration: 700,
      ease: ease.outCubic,
      onUpdate: (_v, t) => {
        disc.carrier.position.set(
          lerp(DIM.platter.x + 4, DIM.platter.x, t),
          lerp(DIM.recordY + 16, DIM.recordY + 0.6, t),
          lerp(DIM.platter.z + 22, DIM.platter.z, t),
        );
        disc.carrier.rotation.x = lerp(0.34, 0.05, t);
        disc.carrier.rotation.z = lerp(-0.14, -0.02, t);
        disc.setOpacity(Math.min(1, t * 2.4));
      },
    }),
  );
  token.check();
}

/** A short damped wobble as the record beds down on the spindle. */
export async function settle(disc: RecordMesh, token: Token): Promise<void> {
  await token.track(
    tween({
      duration: 620,
      ease: ease.linear,
      onUpdate: (_v, t) => {
        const damping = Math.exp(-6 * t);
        disc.carrier.position.y = DIM.recordY + 0.6 * Math.exp(-8 * t);
        disc.carrier.rotation.x = 0.05 * damping * Math.cos(t * 26);
        disc.carrier.rotation.z = -0.02 * damping * Math.sin(t * 22);
      },
    }),
  );
  token.check();
}

/** True when an error is just this swap being superseded by a newer one. */
export const isCancelled = (e: unknown): e is CancelledError => e instanceof CancelledError;
